import React, { useState, useEffect, useCallback } from 'react';
import { fetchCustomCss } from '../utils/cssUploadUtils';
import { get, post } from 'aws-amplify/api';
import { useParams } from 'react-router-dom';
import LoadingSpinner from '../components/LoadingSpinner';
import { renderRecapText } from '../utils/markdownParser';
import '../styles/base.css';
import '../styles/invitation.css';
import '../styles/markdown.css';

function OpenInvitationRecapScreen() {
  const { code } = useParams();
  const [invitation, setInvitation] = useState(null);
  const [event, setEvent] = useState(null);
  const [guests, setGuests] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [password, setPassword] = useState('');
  const [showPasswordForm, setShowPasswordForm] = useState(false);
  const [error, setError] = useState(null);
  const [customCssLoaded, setCustomCssLoaded] = useState(false);

  const loadCustomCss = useCallback(async () => {
    if (event?.customCss && !customCssLoaded) {
      if (event.customCss.startsWith('http://') || event.customCss.startsWith('https://') || event.customCss.startsWith('//')) {
        const styleElement = document.createElement('style');
        styleElement.textContent = `@import url("${event.customCss}");`;
        document.head.appendChild(styleElement);
        setCustomCssLoaded(true);        
      }
      else if (event.customCss.startsWith('/')) {
        const styleElement = document.createElement('style');
        styleElement.textContent = `@import url("${event.customCss}");`;
        document.head.appendChild(styleElement);
        setCustomCssLoaded(true);
      }
      else {
        const cssContent = await fetchCustomCss(event.customCss);
        if (cssContent) {
          const styleElement = document.createElement('style');
          styleElement.textContent = cssContent;
          document.head.appendChild(styleElement);
          setCustomCssLoaded(true);
        }
      }
    }
  }, [event, customCssLoaded]);

  useEffect(() => {
    loadCustomCss();
  }, [loadCustomCss]);

  const fetchInvitation = useCallback(async () => {
    try {
      const token = localStorage.getItem(`invitation_token_${code}`);
      const headers = token ? { Authorization: token } : {};
      
      const restOperation = get({
        apiName: 'events',
        path: `/invitation/${code}`,
        options: { headers }
      });

      const { body } = await restOperation.response;
      const data = await body.json();

      if (!data.invitation) {
        throw new Error('Invitation not found');
      }

      setInvitation(data.invitation);
      setEvent(data.event);
      setGuests(data.guests || []);
      
      if (token) {
        setIsAuthenticated(true);
        setShowPasswordForm(false);
      }

    } catch (error) {
      if (error.response && error.response.status === 401) {
        setShowPasswordForm(true);
        setIsAuthenticated(false);
        setError('This invitation requires a password to view');
      } else {
        setError(error.message || 'Failed to load invitation');
      }
    } finally {
      setIsLoading(false);
    }
  }, [code]);

  useEffect(() => {
    fetchInvitation();
  }, [fetchInvitation]);

  const submitPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError(null);
    
    try {
      const response = await post({
        apiName: 'events',
        path: `/invitation/${code}/authenticate`,
        options: {
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ password })
        }
      });

      const { body } = await response.response;
      const data = await body.json();
      
      if (data.token) {
        localStorage.setItem(`invitation_token_${code}`, data.token);
        setIsAuthenticated(true);
        setShowPasswordForm(false);
        await fetchInvitation();
      }
    } catch (error) {
      setError('Invalid password');
    } finally {
      setIsLoading(false);
    }
  };



  if (isLoading) {
    return <LoadingSpinner message="Loading recap details" />;
  }

  if (error && !showPasswordForm) {
    return (
      <div className="invitation">
        <div className="error-message" role="alert">
          {error}
        </div>
      </div>
    );
  }

  if (showPasswordForm) {
    return (
      <div className="invitation">
        <div className="password-container" role="dialog" aria-labelledby="password-title">
          <h2 id="password-title" className="invitation-title">Protected Invitation</h2>
          <p className="invitation-subtitle">This invitation is password protected. Please enter the password to continue.</p>
          
          <form onSubmit={submitPassword} className="password-form">
            <div className="form-group">
              <label htmlFor="password" className="form">Password</label>
              <input
                type="password"
                id="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="form-input"
                required
              />
            </div>
            
            <button
              type="submit"
              disabled={isLoading}
              className="button button--primary"
            >
              {isLoading ? 'Verifying...' : 'Submit'}
            </button>
          </form>
        </div>
      </div>
    );
  }

  if (!invitation || !event) {
    return (
      <div className="invitation">
        <div className="error-message" role="alert">
          Invitation not found
        </div>
      </div>
    );
  }

  return (
    <div className="invitation">
      <div className="invitation-container">
        <div className="invitation-decorative-element top-left"></div>
        <div className="invitation-decorative-element top-right"></div>
        <div className="invitation-decorative-element bottom-left"></div>
        <div className="invitation-decorative-element bottom-right"></div>
        
        <div className="invitation-header">
          <h3 className="invitation-dearguest">Dear {
            guests.length === 1 ? 
              (guests[0].shortName || guests[0].firstName) :
            guests.length === 2 ?
              `${guests[0].shortName || guests[0].firstName} & ${guests[1].shortName || guests[1].firstName}` :
              guests.map((guest, index) => 
                index === guests.length - 1 ?
                  ` and ${guest.shortName || guest.firstName}` :
                  `${guest.shortName || guest.firstName}${index < guests.length - 2 ? ', ' : ''}`
              ).join('')
          }</h3>

          {event.abovename && (
            <p className="invitation-abovename">{event.abovename}</p>
          )}
          
          <h1 className="invitation-name">
            {event.name.split(' & ').map((part, index, array) => (
              <React.Fragment key={index}>
                {part}
                {index < array.length - 1 && <span className="invitation-name-highlight">&</span>}
              </React.Fragment>
            ))}
          </h1>

          <div className="invitation-content">
            {event.recapText && (
              <div className="invitation-detail-item">
                <span className="invitation-detail-label recap">Event Recap:</span>
                <div className="invitation-detail-value recap">
                  {renderRecapText(event.recapText)}
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default OpenInvitationRecapScreen;